import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  Stethoscope,
  ShieldCheck,
  Users,
  FileText,
  User,
  LogOut,
  Waves,
  Heart,
  Scale,
  HeartHandshake,
  Mic,
  GraduationCap,
  UserSquare2,
  Menu
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    const names = user.full_name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return user.full_name.substring(0, 2).toUpperCase();
  };

  // Get user's tenant name for display
  const getUserTenantName = () => {
    if (!user) return '';
    if (user.role === 'super_admin') return 'All Tenants';
    if (user.tenants && user.tenants.length > 0) {
      return user.tenants[0].name;
    }
    return '';
  };

  const industries = [
    {
      name: 'Medical',
      icon: Heart,
      color: 'text-red-400',
      bg: 'bg-red-500/10 hover:bg-red-500/20 border-red-500/30',
      badge: 'bg-red-500/20 text-red-400 border-red-500/30'
    },
    {
      name: 'Legal',
      icon: Scale,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/30',
      badge: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    },
    {
      name: 'AgedCare',
      icon: HeartHandshake,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/30',
      badge: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
    }
  ];

  const navigationItems = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, current: location.pathname === '/' },
    { name: 'Voice Intake', href: '/voice', icon: Mic, current: location.pathname === '/voice' },
    { name: 'Live Triage', href: '/triage', icon: Stethoscope, current: location.pathname === '/triage' },
    { name: 'Verify & Learn', href: '/verify', icon: ShieldCheck, current: location.pathname === '/verify' },
    { name: 'Training Center', href: '/training', icon: GraduationCap, current: location.pathname === '/training' },
    { name: 'Tenants', href: '/tenants', icon: Users, current: location.pathname === '/tenants' },
    { name: 'Intake Logs', href: '/logs', icon: FileText, current: location.pathname === '/logs' },
    { name: 'Clients', href: '/clients', icon: UserSquare2, current: location.pathname === '/clients' }
  ];

  // Add User Management for super admins only
  if (user?.role === 'super_admin') {
    navigationItems.push({
      name: 'User Management',
      href: '/users',
      icon: User,
      current: location.pathname === '/users'
    });
  }

  const getPageTitle = () => {
    const currentPage = navigationItems.find(item => item.current);
    return currentPage?.name || 'Dashboard';
  };

  // Reusable sidebar content (used by both desktop sidebar and mobile drawer)
  const renderSidebar = (onNavigate) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center px-6 py-6 lg:py-8">
        <div className="flex items-center space-x-2">
          <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-emerald-400 to-cyan-500 rounded-lg">
            <Waves className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg lg:text-xl font-bold text-white leading-tight">DD Universal Voice</h1>
            <p className="text-xs text-slate-400">Professional Platform</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              data-testid={`nav-${item.name.toLowerCase().replace(/[\s&]/g, '-')}`}
              className={cn(
                'flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200',
                item.current ? 'nav-active' : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
              )}
            >
              <Icon className="w-5 h-5 mr-3 shrink-0" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-800/50">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-gradient-to-r from-emerald-400 to-cyan-500 text-white text-sm">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-400 truncate">{getUserTenantName()}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-slate-400 hover:text-white p-1 shrink-0"
            data-testid="logout-button"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        {user?.role && (
          <div className="mt-2 px-3">
            <Badge variant="outline" className="text-xs border-teal-500/30 text-teal-400 bg-teal-500/10">
              {user.role === 'super_admin' && 'Super Admin'}
              {user.role === 'tenant_admin' && 'Tenant Admin'}
              {user.role === 'intake_officer' && 'Intake Officer'}
            </Badge>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-950">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex flex-col w-64 glass-sidebar shrink-0">
        {renderSidebar()}
      </div>

      {/* Mobile Drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 glass-sidebar border-slate-800">
          {renderSidebar(() => setMobileOpen(false))}
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="bg-slate-900 border-b border-slate-800 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            {/* Left: hamburger + title */}
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileOpen(true)}
                className="lg:hidden text-slate-300 hover:text-white p-2 shrink-0"
                data-testid="mobile-menu-button"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h2 className="text-base sm:text-xl font-semibold text-white truncate">
                {getPageTitle()}
              </h2>
            </div>

            {/* Right: industry filter (desktop) + status */}
            <div className="flex items-center gap-3 sm:gap-6">
              {/* Industry Context Switcher - hidden on small screens */}
              <div className="hidden xl:flex items-center space-x-4">
                <span className="text-sm text-slate-400 font-medium">Industry Filter:</span>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedIndustry('All')}
                    className={cn(
                      'text-xs px-3 py-1 rounded-md transition-all duration-200',
                      selectedIndustry === 'All'
                        ? 'bg-slate-600 text-white'
                        : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    )}
                    data-testid="industry-filter-all"
                  >
                    All Industries
                  </Button>
                  {industries.map((industry) => {
                    const Icon = industry.icon;
                    const isSelected = selectedIndustry === industry.name;
                    return (
                      <Button
                        key={industry.name}
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedIndustry(industry.name)}
                        className={cn(
                          'text-xs px-3 py-1 rounded-md border transition-all duration-200 flex items-center space-x-1',
                          isSelected ? industry.bg : 'border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700/50'
                        )}
                        data-testid={`industry-filter-${industry.name.toLowerCase()}`}
                      >
                        <Icon className={cn('w-3 h-3', isSelected ? industry.color : 'text-slate-500')} />
                        <span>{industry.name}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* System Status */}
              <div className="flex items-center space-x-2 shrink-0">
                <div className="w-2 h-2 bg-emerald-400 rounded-full pulse-glow"></div>
                <span className="hidden sm:inline text-sm text-slate-400">System Online</span>
              </div>
            </div>
          </div>

          {/* Industry filter - mobile/tablet (horizontally scrollable row) */}
          <div className="xl:hidden mt-3 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIndustry('All')}
              className={cn(
                'text-xs px-3 py-1 rounded-md transition-all duration-200 shrink-0',
                selectedIndustry === 'All'
                  ? 'bg-slate-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              )}
            >
              All Industries
            </Button>
            {industries.map((industry) => {
              const Icon = industry.icon;
              const isSelected = selectedIndustry === industry.name;
              return (
                <Button
                  key={industry.name}
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIndustry(industry.name)}
                  className={cn(
                    'text-xs px-3 py-1 rounded-md border transition-all duration-200 flex items-center space-x-1 shrink-0',
                    isSelected ? industry.bg : 'border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700/50'
                  )}
                >
                  <Icon className={cn('w-3 h-3', isSelected ? industry.color : 'text-slate-500')} />
                  <span>{industry.name}</span>
                </Button>
              );
            })}
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto bg-slate-950 p-4 sm:p-6">
          <Outlet context={{ selectedIndustry, setSelectedIndustry }} />
        </main>
      </div>
    </div>
  );
};

export default Layout;
